import * as React from 'react';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Table, TableHead, TableBody, TableRow, TableCell, CircularProgress } from '@mui/material';
import { fetchMonthlyBillingDetail, fetchDailyBillingDetail } from '../../../api';
import './MybillDialog.css';

interface BillingDataForMonthlyBill {
  Year: number;
  MonthNm: string;
  OpenAI: number;
  BARD: number;
  GenieDocs: number;
  Total_Cost: number;
}

interface BillingDataForDailyBill {
  Date: string;
  DayNm: string;
  Total_Cost: number;
  OpenAI: number;
  BARD: number;
  GenieDocs: number;
}

interface CustomTabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: CustomTabPanelProps) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index: any) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function BasicTabs() {
  const [value, setValue] = React.useState(0);
  const [monthlyBillingData, setMonthlyBillingData] = React.useState<BillingDataForMonthlyBill[]>([]);
  const [dailyBillingData, setDailyBillingData] = React.useState<BillingDataForDailyBill[]>([]);
  const [loading, setLoading] = React.useState(true); 

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true); // Start loading
        const [monthlyData, dailyData] = await Promise.all([
          fetchMonthlyBillingDetail(),
          fetchDailyBillingDetail(),
        ]);
        setMonthlyBillingData(monthlyData);
        setDailyBillingData(dailyData);
      } catch (error) {
        console.error('Error fetching billing details:', error);
      } finally {
        setLoading(false); 
      }
    };

    fetchData();
  }, []);

  const calculateMonthlyTotal = () => {
    let totalTotalCost = 0;
    let totalOpenAI = 0;
    let totalBARD = 0;
    let totalGenieDoc = 0;

    monthlyBillingData.forEach((item) => {
      totalTotalCost += item.Total_Cost;
      totalOpenAI += item.OpenAI;
      totalBARD += item.BARD;
      totalGenieDoc += item.GenieDocs;
    });
    return { totalOpenAI, totalBARD, totalGenieDoc, totalTotalCost: parseFloat(totalTotalCost.toFixed(2)) };
  };

  const calculateDailyTotal = () => {
    let totalTotalCost = 0;
    let totalOpenAI = 0;
    let totalBARD = 0;
    let totalGenieDoc = 0;

    dailyBillingData.forEach((item) => {
      totalTotalCost += item.Total_Cost;
      totalOpenAI += item.OpenAI;
      totalBARD += item.BARD;
      totalGenieDoc += item.GenieDocs;
    });
    return { totalTotalCost: parseFloat(totalTotalCost.toFixed(2)), totalOpenAI, totalBARD, totalGenieDoc };
  };

  const monthlyTotal = calculateMonthlyTotal();
  const dailyTotal = calculateDailyTotal();

  const createTableMonthlyRows = (data: BillingDataForMonthlyBill[]) => {
    return data.map((row, index) => (
      <TableRow key={index}>
        <TableCell>{row.Year}</TableCell>
        <TableCell>{row.MonthNm}</TableCell>
        <TableCell>{row.BARD}</TableCell>
        <TableCell>{row.OpenAI}</TableCell>
         {/* <TableCell>{row.GenieDocs}</TableCell> */}
        <TableCell>{row.Total_Cost.toFixed(2)}</TableCell>
      </TableRow>
    ));
  };

  const createTableDailyRows = (data: BillingDataForDailyBill[]) => {
    return data.map((row, index) => (
      <TableRow key={index}>
        <TableCell>{row.DayNm}</TableCell>
        <TableCell>{row.Date}</TableCell>
        <TableCell>{row.BARD}</TableCell>
        <TableCell>{row.OpenAI}</TableCell>
        {/* <TableCell>{row.GenieDocs}</TableCell> */}
        <TableCell>{row.Total_Cost.toFixed(2)}</TableCell>
      </TableRow>
    ));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '30rem' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
          <Tab label="Date Wise Billing (Last 30 Days)" {...a11yProps(0)} />
          <Tab label="Year Wise Billing (Last 12 Months)" {...a11yProps(1)} />
        </Tabs>
      </Box>

      <CustomTabPanel value={value} index={0}>
        <div className='tableBody monthlyScroll'>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Day Name</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>GPT-4oMini (Tokens)</TableCell>
                <TableCell>GPT-4o (Tokens)</TableCell>
                {/* <TableCell>GPT4(Tokens)</TableCell> */}
                <TableCell>Total Cost (In Rupees)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {createTableDailyRows(dailyBillingData)}
            </TableBody>
          </Table>
        </div>

        <div className="footerRow monthlyfooter">
          <table>
            <tr>
              <td className="MonthfooterWidth_td"><strong>Total</strong></td>
              <td className="MonthfooterWidth_td"></td>
              <td className="MonthfooterWidth_td"><strong>{dailyTotal.totalBARD}</strong></td>
              <td className="MonthfooterWidth_td"><strong>{dailyTotal.totalOpenAI}</strong></td>
              {/* <td className="MonthfooterWidth_td"><strong>{dailyTotal.totalGenieDoc}</strong></td> */}
              <td className="MonthfooterWidth_td"><strong>{dailyTotal.totalTotalCost}</strong></td>
            </tr>
          </table>
        </div>
      </CustomTabPanel>

      <CustomTabPanel value={value} index={1}>
        <div className='tableBody'>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Year</TableCell>
                <TableCell>Month</TableCell>
                <TableCell>GPT-4o Mini (Tokens)</TableCell>
                <TableCell>GPT-4o (Tokens)</TableCell>
                {/* <TableCell>GPT4(Tokens)</TableCell> */}
                <TableCell>Total Cost (In Rupees)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {createTableMonthlyRows(monthlyBillingData)}
            </TableBody>
          </Table>
        </div>
        <div className="footerRow">
          <table>
            <tr>
              <td style={{width:"17%"}}><strong>Total</strong></td>
              <td className="YearfooterWidth_td"></td>
              <td className="YearfooterWidth_td"><strong>{monthlyTotal.totalBARD}</strong></td>
              <td className="YearfooterWidth_td"><strong>{monthlyTotal.totalOpenAI}</strong></td>
              {/* <td className="YearfooterWidth_td"><strong>{monthlyTotal.totalGenieDoc}</strong></td> */}
              <td className="YearfooterWidth_td"><strong>{monthlyTotal.totalTotalCost}</strong></td>
            </tr>
          </table>
        </div>
      </CustomTabPanel>
    </Box>
  );
}
